import React, { FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { channelSocialLinksSelector } from 'features/channel/channelSelectors';
import { removePunctuation } from 'utilities/formatters';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { IconButton } from 'components/Buttons/IconButton';

interface SocialLinksProps {
  iconList?: boolean;
}

export const ChannelSocialLinks: FunctionComponent<SocialLinksProps> = (
  props
) => {
  const channelSocialLinks = useSelector(channelSocialLinksSelector);

  // social-links (~30 KB) is only needed to detect a profile name for Website /
  // Blog entries. Load it on demand so it stays out of the entry bundle; until
  // it resolves (or for channels with no Website/Blog links) we fall back to the
  // plain link name.
  const [detectProfile, setDetectProfile] = useState<
    ((link: string) => string | false) | null
  >(null);

  const hasWebsiteOrBlog = !!channelSocialLinks?.some(
    (link) => link.name === 'Website' || link.name === 'Blog'
  );

  useEffect(() => {
    if (!hasWebsiteOrBlog || detectProfile) return;
    let active = true;
    import('social-links').then(({ SocialLinks }) => {
      if (!active) return;
      const socialLinks = new SocialLinks();
      setDetectProfile(() => (link: string) => socialLinks.detectProfile(link));
    });
    return () => {
      active = false;
    };
  }, [hasWebsiteOrBlog, detectProfile]);

  const renderIcon = (name: string, link: string) => {
    // If creator adds a social link within blog / website
    if ((name === 'Website' || name === 'Blog') && detectProfile) {
      const profileName = detectProfile(link);
      if (profileName) return profileName;
    }
    return removePunctuation(name).toLowerCase();
  };

  if (!channelSocialLinks) {
    return null;
  }

  return (
    <ul
      className={classNames('list--reset', styles['social-links'], {
        [styles['social-links--icons']]: props.iconList
      })}
    >
      {channelSocialLinks.map((link) => (
        <li key={link.url}>
          <IconButton
            showLabel={!props.iconList}
            icon={renderIcon(link.name, link.url)}
            label={link.url}
            href={link.url}
            external={true}
            noBackground={true}
            target="_blank"
            rel="ugc nofollow noopener noreferrer"
          />
        </li>
      ))}
    </ul>
  );
};
